import React, { useState, useEffect } from 'react';

import { 
  CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Cell, ReferenceLine, ScatterChart, Scatter, ZAxis
} from 'recharts';
import { Upload, TrendingDown, TrendingUp, DollarSign, Settings2, Link, Trash2, Clock, RefreshCw } from 'lucide-react';
import { CompetitorLinksModal } from './CompetitorLinksModal';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const PricingDashboard: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeProduct, setActiveProduct] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [config, setConfig] = useState({ hour: '02:00', active: true });
  const [savingConfig, setSavingConfig] = useState(false);
  const [scrapingSelected, setScrapingSelected] = useState(false);

  const handleDelete = async (ids: number[]) => {
    if (!window.confirm(`Tem certeza que deseja deletar ${ids.length > 1 ? 'estes produtos' : 'este produto'}?`)) return;
    
    try {
      const token = localStorage.getItem('tt_auth_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/products`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids })
      });
      if (res.ok) {
        setSelectedRows([]);
        refreshData();
      } else {
        alert('Erro ao deletar.');
      }
    } catch (e) {
      console.error(e);
      alert('Erro de conexão ao deletar.');
    }
  };

  const handleScrapeSelected = async (ids: number[]) => {
    if (!window.confirm(`Tem certeza que deseja extrair os preços dos concorrentes para ${ids.length > 1 ? 'estes produtos' : 'este produto'}?`)) return;
    
    try {
      setScrapingSelected(true);
      const token = localStorage.getItem('tt_auth_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/scrape-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids })
      });
      if (res.ok) {
        const result = await res.json();
        alert(`Scraping concluído!\nSucesso: ${result.successes}\nFalhas: ${result.failures}`);
        setSelectedRows([]);
        refreshData();
      } else {
        alert('Erro ao executar scrape múltiplo.');
      }
    } catch (e) {
      console.error(e);
      alert('Erro de conexão ao iniciar scrape.');
    } finally {
      setScrapingSelected(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('tt_auth_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/settings`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setConfig(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveConfig = async (newConfig: any) => {
    setConfig(newConfig);
    setSavingConfig(true);
    try {
      const token = localStorage.getItem('tt_auth_token') || localStorage.getItem('token');
      await fetch(`${API_URL}/api/pricing/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newConfig)
      });
    } catch(e) {
      console.error(e);
    } finally {
      setSavingConfig(false);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('tt_auth_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const resData = await res.json();
      setData(resData);
    } catch (error) {
      console.error('Failed to fetch pricing data', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      const token = localStorage.getItem('tt_auth_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        
        if (activeProduct) {
          const updatedProduct = json.tableData.find((p: any) => p.id === activeProduct.id);
          if (updatedProduct) setActiveProduct(updatedProduct);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      const token = localStorage.getItem('tt_auth_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/upload-csv`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      const resData = await res.json();
      alert(`${resData.imported} produtos importados/atualizados com sucesso!`);
      fetchData();
    } catch (err) {
      alert('Erro ao importar CSV.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div style={{ color: 'white', padding: 20 }}>Carregando dados de pricing...</div>;

  const scatterData = data?.scatterData?.map((d: any) => ({
    ...d, 
    diff: d.x > 0 ? Number((((d.y - d.x) / d.x) * 100).toFixed(1)) : 0,
    shortName: d.name.length > 25 ? d.name.substring(0, 25) + '...' : d.name
  })) || [];

  const cheapestTop15 = [...scatterData].filter(d => d.diff > 0).sort((a, b) => b.diff - a.diff).slice(0, 15);
  const expensiveTop15 = [...scatterData].filter(d => d.diff < 0).sort((a, b) => a.diff - b.diff).slice(0, 15);

  return (
    <div style={{ padding: '24px', paddingTop: '160px', color: 'var(--text-primary)', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 20 }}>
        <h1>Monitoramento de Preços</h1>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-secondary)', padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border-color)' }}>
            <Clock size={18} color="var(--text-secondary)" />
            <span style={{ fontSize: 14 }}>Robô Automático (Brasília):</span>
            <input 
              type="time" 
              value={config.hour} 
              onChange={(e) => handleSaveConfig({ ...config, hour: e.target.value })}
              style={{ background: 'var(--bg-primary)', color: 'white', border: '1px solid var(--border-color)', borderRadius: 4, padding: '4px 8px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 14, marginLeft: 10 }}>
              <input 
                type="checkbox" 
                checked={config.active}
                onChange={(e) => handleSaveConfig({ ...config, active: e.target.checked })}
              />
              Ativo
            </label>
            {savingConfig && <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 10 }}>Salvando...</span>}
          </div>

          <div style={{ position: 'relative' }}>
          <input 
            type="file" 
            accept=".csv"
            id="csv-upload"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            disabled={uploading}
          />
          <label htmlFor="csv-upload" style={{
            background: 'var(--primary-color)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            opacity: uploading ? 0.7 : 1
          }}>
            <Upload size={18} />
            {uploading ? 'Importando...' : 'Importar CSV Bling'}
          </label>
        </div>
      </div>
    </div>

      {/* KPIs */}
      {data?.kpis && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 30 }}>
          <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 12, border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <TrendingUp color="#10b981" />
              <h3>Mais Baratos (TechTennis)</h3>
            </div>
            <p style={{ fontSize: 32, fontWeight: 'bold', color: '#10b981' }}>{data.kpis.totalCheaper}</p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Nossos preços estão menores</p>
          </div>
          
          <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 12, border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <TrendingDown color="#ef4444" />
              <h3>Mais Caros</h3>
            </div>
            <p style={{ fontSize: 32, fontWeight: 'bold', color: '#ef4444' }}>{data.kpis.totalMoreExpensive}</p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Concorrência com preço melhor</p>
          </div>

          <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 12, border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <DollarSign color="#f59e0b" />
              <h3>Oportunidades de Margem</h3>
            </div>
            <p style={{ fontSize: 32, fontWeight: 'bold', color: '#f59e0b' }}>{data.kpis.totalOpportunity}</p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Nós estamos &gt;15% mais baratos</p>
          </div>
        </div>
      )}

      {/* Gráficos Executivos */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 30 }}>
        
        {/* Gráfico 1: Dispersão (Visão Geral) */}
        <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 12, border: '1px solid var(--border-color)' }}>
          <h3 style={{ marginBottom: 5 }}>1. Visão Geral de Preços (TechTennis vs Concorrência)</h3>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>Eixo X: Nosso Preço (R$) | Eixo Y: Preço Concorrente (R$). A linha diagonal representa igualdade de preços. Pontos acima da linha somos mais baratos.</p>
          <div style={{ height: 350, width: '100%' }}>
            <ResponsiveContainer>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" dataKey="x" name="TechTennis" unit="R$" stroke="#94a3b8" />
                <YAxis type="number" dataKey="y" name="Concorrente" unit="R$" stroke="#94a3b8" />
                <ZAxis type="category" dataKey="name" name="Produto" />
                <RechartsTooltip cursor={{ strokeDasharray: '3 3' }} 
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                />
                <ReferenceLine x={0} y={0} />
                <Scatter name="Produtos" data={scatterData} fill="#8884d8">
                  {scatterData.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.diff > 0 ? '#10b981' : entry.diff < 0 ? '#ef4444' : '#64748b'} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Gráfico 2: Top 15 Oportunidades */}
          <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 12, border: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: 5 }}>2. Top 15 Oportunidades</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>Produtos onde estamos significativamente mais baratos (em %).</p>
            <div style={{ height: 400, width: '100%' }}>
              <ResponsiveContainer>
                <BarChart data={cheapestTop15} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                  <XAxis type="number" domain={[0, 'auto']} tickFormatter={(val) => `${val}%`} stroke="#94a3b8" />
                  <YAxis type="category" dataKey="shortName" stroke="#94a3b8" width={180} tick={{ fontSize: 11 }} />
                  <RechartsTooltip 
                    cursor={{ fill: 'rgba(255,255,255,0.05)' }} 
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#fff' }}
                    formatter={(value: any) => [`+${value}%`, 'Mais barato']}
                  />
                  <Bar dataKey="diff" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gráfico 3: Top 15 Alertas */}
          <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 12, border: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: 5 }}>3. Top 15 Alertas de Preço</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>Produtos onde estamos mais caros que a concorrência (em %).</p>
            <div style={{ height: 400, width: '100%' }}>
              <ResponsiveContainer>
                <BarChart data={expensiveTop15} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                  <XAxis type="number" domain={['auto', 0]} tickFormatter={(val) => `${val}%`} stroke="#94a3b8" />
                  <YAxis type="category" dataKey="shortName" stroke="#94a3b8" width={180} tick={{ fontSize: 11 }} />
                  <RechartsTooltip 
                    cursor={{ fill: 'rgba(255,255,255,0.05)' }} 
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#fff' }}
                    formatter={(value: any) => [`${value}%`, 'Mais caro']}
                  />
                  <Bar dataKey="diff" fill="#ef4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Mapeamento */}
      <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 12, border: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0 }}>Painel de Preços & Ações</h3>
          {selectedRows.length > 0 && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button 
                onClick={() => handleScrapeSelected(selectedRows)}
                disabled={scrapingSelected}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: scrapingSelected ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 500,
                  opacity: scrapingSelected ? 0.7 : 1
                }}
              >
                <RefreshCw size={16} className={scrapingSelected ? 'spin' : ''} /> {scrapingSelected ? 'Atualizando...' : `Atualizar Selecionados (${selectedRows.length})`}
              </button>
              <button 
                onClick={() => handleDelete(selectedRows)}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 500
                }}
              >
                <Trash2 size={16} /> Deletar Selecionados ({selectedRows.length})
              </button>
            </div>
          )}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: 12, width: 40 }}>
                  <input 
                    type="checkbox" 
                    onChange={(e) => {
                      if (e.target.checked && data?.tableData) {
                        setSelectedRows(data.tableData.map((item: any) => item.id));
                      } else {
                        setSelectedRows([]);
                      }
                    }}
                    checked={data?.tableData?.length > 0 && selectedRows.length === data.tableData.length}
                  />
                </th>
                <th style={{ padding: 12 }}>Produto</th>
                <th style={{ padding: 12 }}>Nosso Preço</th>
                <th style={{ padding: 12 }}>Links</th>
                <th style={{ padding: 12 }}>Melhor Preço</th>
                <th style={{ padding: 12 }}>Diferença</th>
                <th style={{ padding: 12 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {data?.tableData?.map((item: any) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #334155' }}>
                  <td style={{ padding: 12 }}>
                    <input 
                      type="checkbox" 
                      checked={selectedRows.includes(item.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedRows([...selectedRows, item.id]);
                        else setSelectedRows(selectedRows.filter(id => id !== item.id));
                      }}
                    />
                  </td>
                  <td style={{ padding: 12 }}>
                    <strong>{item.productName}</strong><br/>
                    <small style={{ color: 'var(--text-secondary)' }}>SKU: {item.sku}</small>
                  </td>
                  <td style={{ padding: 12, fontWeight: 500 }}>R$ {item.techTennisPrice.toFixed(2)}</td>
                  <td style={{ padding: 12 }}>
                    {item.linkCount > 0 ? (
                      <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{item.linkCount} lojas</span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>Nenhum</span>
                    )}
                  </td>
                  <td style={{ padding: 12 }}>
                    {item.bestCompetitorPrice > 0 ? `R$ ${item.bestCompetitorPrice.toFixed(2)}` : '-'}
                  </td>
                  <td style={{ padding: 12 }}>
                    {item.status === 'CHEAPER' && <span style={{ color: '#10b981', fontWeight: 600 }}>-{Math.abs(item.diffPercent).toFixed(1)}%</span>}
                    {item.status === 'EXPENSIVE' && <span style={{ color: '#ef4444', fontWeight: 600 }}>+{item.diffPercent.toFixed(1)}%</span>}
                    {item.status === 'EQUAL' && <span style={{ color: '#94a3b8', fontWeight: 600 }}>0%</span>}
                    {item.status === 'UNMAPPED' && <span style={{ color: 'var(--text-secondary)' }}>-</span>}
                    {item.status === 'NO_DATA' && <span style={{ color: '#f59e0b' }}>Pendente</span>}
                  </td>
                  <td style={{ padding: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button 
                        onClick={() => setActiveProduct(item)}
                        style={{
                          background: item.linkCount === 0 ? '#3b82f6' : 'transparent',
                          border: item.linkCount === 0 ? 'none' : '1px solid var(--border-color)',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: 6,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontWeight: item.linkCount === 0 ? 600 : 400
                        }}
                      >
                        {item.linkCount === 0 ? <Link size={14} /> : <Settings2 size={14} />}
                        {item.linkCount === 0 ? 'Vincular Link' : 'Gerenciar'}
                      </button>
                      
                      <button 
                        onClick={() => handleDelete([item.id])}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--border-color)',
                          color: '#ef4444',
                          padding: '6px 12px',
                          borderRadius: 6,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Deletar Produto"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!data?.tableData || data.tableData.length === 0) && (
                <tr>
                  <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Nenhum produto encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {activeProduct && (
        <CompetitorLinksModal 
          product={activeProduct} 
          onClose={() => setActiveProduct(null)} 
          onRefresh={refreshData}
        />
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};
